// "Should I file a claim or pay out of pocket?" — an opt-in card on the results
// page, collapsed to a single headline and a button until the user asks for it.
//
// Why here and not in the wizard: the repair range is the input to this
// decision, so it can't be asked earlier, and most people only want a number.
// Behind the button the questions are adaptive — an "other driver" answer
// settles it in one tap and "liability only" in two, so the full four fire only
// for at-fault-with-collision-coverage, which is exactly the case where the
// arithmetic is genuinely close.
//
// Nothing here is sent to the server or written into the shared report: these
// answers are the user's finances, not their crash.
import { useState } from "react";
import type {
  ClaimAnswers,
  ClaimDecision,
  ClaimMath,
  ClaimQuestionId,
  Estimate,
  VehicleValueRange,
} from "../shared/types";
import {
  CLAIM_COPY,
  CLAIM_QUESTIONS,
  CLAIM_QUESTION_ORDER,
  assessClaim,
  claimDecisionApplies,
  nextClaimQuestion,
  resetFrom,
} from "../shared/claimDecision";
import {
  COVERAGE_ANSWERS,
  DEDUCTIBLE_ANSWERS,
  FAULT_ANSWERS,
  OWNERSHIP_ANSWERS,
  type Option,
} from "../shared/questionnaire";

const usd = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

// Deductible ids are strings because "I don't know" isn't a number. `null` is
// the answered-but-unknown case, which the engine treats as its assumed
// deductible and always discloses.
const deductibleFromId = (id: string): number | null => (id === "unknown" ? null : Number(id));
const deductibleToId = (v: number | null | undefined): string =>
  v === null ? "unknown" : v === undefined ? "" : String(v);

const OPTIONS: Record<ClaimQuestionId, Option<string>[]> = {
  fault: FAULT_ANSWERS,
  coverage: COVERAGE_ANSWERS,
  deductible: DEDUCTIBLE_ANSWERS,
  ownership: OWNERSHIP_ANSWERS,
};

// The chosen answer as an option id, for the recap chips.
function answeredId(a: ClaimAnswers, q: ClaimQuestionId): string {
  if (q === "deductible") return deductibleToId(a.deductible);
  return (a[q] as string | undefined) ?? "";
}

function labelFor(q: ClaimQuestionId, id: string): string {
  return OPTIONS[q].find((o) => o.id === id)?.label ?? id;
}

// Where the break-even bill sits inside the repair range. This is the whole
// decision in one picture for the close-call case: the range is what the repair
// might cost, the marker is the point above which filing starts winning.
function BreakEvenBar({ math }: { math: ClaimMath }) {
  const max = Math.max(math.cashCost.high, math.claimCost) * 1.08;
  const pct = (n: number) => `${Math.max(0, Math.min(100, (n / max) * 100))}%`;
  const inRange = math.breakEven >= math.cashCost.low && math.breakEven <= math.cashCost.high;
  return (
    <div className="be">
      <div className="be-track">
        <div
          className="be-range"
          style={{ left: pct(math.cashCost.low), width: pct(math.cashCost.high - math.cashCost.low) }}
        />
        <div className="be-mark" style={{ left: pct(math.breakEven) }} />
      </div>
      <div className="be-legend">
        <span className="be-key be-key-range">
          Repair range {usd(math.cashCost.low)} – {usd(math.cashCost.high)}
        </span>
        <span className="be-key be-key-mark">
          {inRange ? "Break-even" : "Cost of filing"} {usd(math.breakEven)}
        </span>
      </div>
    </div>
  );
}

function MathTable({ math }: { math: ClaimMath }) {
  return (
    <details className="claim-math">
      <summary>How we got {usd(math.claimCost)}</summary>
      <dl>
        <div>
          <dt>Deductible{math.deductibleAssumed ? " (assumed)" : ""}</dt>
          <dd>{usd(math.deductible)}</dd>
        </div>
        <div>
          <dt>Premium increase{math.premiumAssumed ? " (regional average)" : ""}</dt>
          <dd>
            {usd(math.surchargeAnnual)}/yr × {math.surchargeYears} yrs = {usd(math.surchargeTotal)}
          </dd>
        </div>
        <div className="claim-math-total">
          <dt>Cost of filing</dt>
          <dd>{usd(math.claimCost)}</dd>
        </div>
      </dl>
      <p className="sub">
        Based on a {usd(math.annualPremium)}/yr full-coverage premium
        {math.premiumAssumed ? " (a regional average; yours is what matters)" : ""}. Averages, not
        your carrier's actual rating.
      </p>
    </details>
  );
}

function Verdict({
  decision,
  hasValue,
  onReset,
}: {
  decision: ClaimDecision;
  // Whether the total-loss card above has a value to work from yet. Only drives
  // the pointer below — the value itself reaches the math through answers.
  hasValue: boolean;
  onReset: () => void;
}) {
  const { math } = decision;

  return (
    <div className={`claim-verdict cv-${decision.verdict}`}>
      {decision.totalLossWarning && (
        <p className="claim-totalloss" role="alert">
          {decision.totalLossWarning}
        </p>
      )}

      <h3>{decision.headline}</h3>
      <p className="claim-summary">{decision.summary}</p>

      {math && <BreakEvenBar math={math} />}
      {math && <MathTable math={math} />}

      <ul className="claim-points">
        {decision.points.map((p, i) => (
          <li key={i}>{p}</li>
        ))}
      </ul>

      <p className="claim-next">
        <b>Next step:</b> {decision.nextStep}
      </p>

      {/* The car's value used to be asked for here as a fifth, optional
          question. It now belongs to the total-loss card above, which asks it
          properly and can estimate it — so this card reads that answer and
          points at it when there isn't one yet. Asking twice on one page for
          the same number would be the worse failure. */}
      {math && !hasValue && (
        <p className="claim-extra sub">
          Want the total-loss check folded into this? Answer "what's the car worth"
          in the card above and it'll factor in here too.
        </p>
      )}

      <p className="claim-disclaimer">{decision.disclaimer}</p>

      <button className="claim-restart" onClick={onReset}>
        Change my answers
      </button>
    </div>
  );
}

export function ClaimOrCash({
  estimate,
  insurerId,
  vehicleValue,
}: {
  estimate: Estimate;
  insurerId?: string;
  // Answered on the total-loss card above, not here. Its midpoint feeds the
  // heads-up in claim-rules.json that fires when a repair approaches the
  // total-loss line — past that point this card's question stops being the
  // relevant one, and saying so is worth more than a silent verdict.
  vehicleValue?: VehicleValueRange | null;
}) {
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState<ClaimAnswers>({});
  const withValue: ClaimAnswers = vehicleValue
    ? { ...answers, vehicleValue: Math.round((vehicleValue.low + vehicleValue.high) / 2) }
    : answers;

  // Nothing to weigh when the engine fell through to its inspection-only path:
  // there's no repair scope, so there's no decision yet.
  if (!claimDecisionApplies(estimate)) return null;

  // They already told us at step 1 that they're paying out of pocket. Asking
  // them to re-answer it as a question would be worse than useless.
  if (insurerId === "uninsured") {
    const v = CLAIM_COPY.verdicts["cash-only"];
    return (
      <div className="card claim-card">
        <h2>{CLAIM_COPY.uninsuredHeadline}</h2>
        <p className="sub">{CLAIM_COPY.uninsuredSummary}</p>
        <ul className="claim-points">
          {v.points.map((p, i) => (
            <li key={i}>{p}</li>
          ))}
        </ul>
        <p className="claim-next">
          <b>Next step:</b> {v.nextStep}
        </p>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="card claim-card">
        <h2>{CLAIM_COPY.teaserHeadline}</h2>
        <p className="sub">{CLAIM_COPY.teaserSummary}</p>
        <div className="estimate-cta">
          <button className="cta" onClick={() => setOpen(true)}>
            {CLAIM_COPY.teaserButton}
          </button>
        </div>
      </div>
    );
  }

  const pending = nextClaimQuestion(answers);
  const answered = CLAIM_QUESTION_ORDER.filter((q) => answeredId(answers, q) !== "");

  return (
    <div className="card claim-card">
      <h2>{CLAIM_COPY.teaserHeadline}</h2>

      {/* Recap chips. Tapping one re-opens that question and drops everything
          downstream of it — the later questions are only reachable through the
          earlier ones, so keeping them would leave a stale answer in the math. */}
      {answered.length > 0 && (
        <div className="claim-chips">
          {answered.map((q) => (
            <button
              key={q}
              className="claim-chip"
              onClick={() => setAnswers(resetFrom(answers, q))}
              title="Change this answer"
            >
              {labelFor(q, answeredId(answers, q))}
              <span aria-hidden="true"> ✕</span>
            </button>
          ))}
        </div>
      )}

      {pending ? (
        <div className="claim-q">
          <h3>{CLAIM_QUESTIONS[pending].prompt}</h3>
          <p className="sub">{CLAIM_QUESTIONS[pending].hint}</p>
          <div className="options">
            {OPTIONS[pending].map((o) => (
              <button
                key={o.id}
                className="opt"
                onClick={() =>
                  setAnswers((prev) =>
                    pending === "deductible"
                      ? { ...prev, deductible: deductibleFromId(o.id) }
                      : { ...prev, [pending]: o.id },
                  )
                }
              >
                <span>{o.label}</span>
                {o.hint && <span className="opt-hint">{o.hint}</span>}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <Verdict
          decision={assessClaim(estimate, withValue)}
          hasValue={!!vehicleValue}
          onReset={() => setAnswers({})}
        />
      )}
    </div>
  );
}
